import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Avatar from '@mui/material/Avatar';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import type { AgentMessage, QuickReply } from '../types';

function renderMarkdown(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <Box key={i} component="span" sx={{ fontWeight: 700 }}>
          {part.slice(2, -2)}
        </Box>
      );
    }
    return part;
  });
}

interface ChatMessageProps {
  message: AgentMessage;
  onQuickReply: (value: string, label: string) => void;
  isLast: boolean;
}

export default function ChatMessage({ message, onQuickReply, isLast }: ChatMessageProps) {
  const isAgent = message.role === 'agent';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isAgent ? 'row' : 'row-reverse',
        alignItems: 'flex-end',
        gap: 1,
        mb: 1.5,
      }}
    >
      <Avatar
        sx={{
          width: 30,
          height: 30,
          flexShrink: 0,
          bgcolor: isAgent ? 'primary.main' : 'grey.300',
          mb: 0.25,
        }}
      >
        {isAgent ? (
          <AutoAwesomeIcon sx={{ fontSize: 16 }} />
        ) : (
          <PersonOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
        )}
      </Avatar>

      <Box
        sx={{
          maxWidth: '78%',
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          alignItems: isAgent ? 'flex-start' : 'flex-end',
        }}
      >
        <Box
          sx={{
            px: 1.75,
            py: 1.25,
            borderRadius: isAgent ? '4px 12px 12px 12px' : '12px 4px 12px 12px',
            bgcolor: isAgent ? 'primary.main' : 'grey.100',
            color: isAgent ? 'primary.contrastText' : 'text.primary',
            boxShadow: 1,
          }}
        >
          <Typography variant="body2" sx={{ lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
            {message.content.split('\n').map((line, i) => (
              <span key={i}>
                {i > 0 && <br />}
                {renderMarkdown(line)}
              </span>
            ))}
          </Typography>
        </Box>

        {isAgent && isLast && message.quickReplies && message.quickReplies.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 0.25 }}>
            {message.quickReplies.map((qr: QuickReply) => (
              <Chip
                key={qr.value}
                label={qr.label}
                size="small"
                variant="outlined"
                onClick={() => onQuickReply(qr.value, qr.label)}
                sx={{
                  cursor: 'pointer',
                  borderColor: 'primary.main',
                  color: 'primary.main',
                  fontWeight: 500,
                  fontSize: '0.72rem',
                  height: 26,
                  '&:hover': { bgcolor: 'primary.main', color: 'primary.contrastText' },
                  transition: 'all 0.15s',
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}
